import { createAsyncThunk } from "@reduxjs/toolkit";
import { teamsApi } from "../api/teams.api";
import type {
  AddTeamMemberRequest,
  CreateTeamRequest,
  TeamRole,
  UpdateTeamRequest,
} from "../domain/team.types";
import type { TeamsState } from "./teams.slice";
import {
  memberAdded,
  memberRemoved,
  membersLoaded,
  membersRequestFailed,
  membersRequestStarted,
  myMembershipsLoaded,
  myMembershipsRequestFailed,
  myMembershipsRequestStarted,
  teamAdded,
  teamUpdated,
  teamsLoaded,
  teamsRequestFailed,
  teamsRequestStarted,
} from "./teams.slice";

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export const fetchTeams = createAsyncThunk<void, void, { state: { teams: TeamsState } }>(
  "teams/fetchTeams",
  async (_, { dispatch }) => {
    dispatch(teamsRequestStarted());
    try {
      const teams = await teamsApi.list();
      dispatch(teamsLoaded(teams));
    } catch (err) {
      dispatch(teamsRequestFailed(errorMessage(err)));
      throw err;
    }
  },
  {
    // Every page that needs teams calls this unconditionally on mount - with
    // no cache, navigating between them re-fetches the full team list (which
    // only grows) from scratch every time. Once we have it, reuse it; a
    // create/update goes through teamAdded/teamUpdated instead of a refetch.
    condition: (_, { getState }) => {
      const { status } = getState().teams;
      return status !== "succeeded" && status !== "loading";
    },
  },
);

export const createTeam = createAsyncThunk(
  "teams/createTeam",
  async (payload: CreateTeamRequest, { dispatch }) => {
    const team = await teamsApi.create(payload);
    dispatch(teamAdded(team));
  },
);

export const updateTeam = createAsyncThunk(
  "teams/updateTeam",
  async ({ teamId, payload }: { teamId: string; payload: UpdateTeamRequest }, { dispatch }) => {
    const team = await teamsApi.update(teamId, payload);
    dispatch(teamUpdated(team));
  },
);

export const fetchTeamMembers = createAsyncThunk(
  "teams/fetchMembers",
  async (teamId: string, { dispatch }) => {
    dispatch(membersRequestStarted());
    try {
      const members = await teamsApi.listMembers(teamId);
      dispatch(membersLoaded({ teamId, members }));
    } catch (err) {
      dispatch(membersRequestFailed(errorMessage(err)));
      throw err;
    }
  },
);

export const addTeamMember = createAsyncThunk(
  "teams/addMember",
  async ({ teamId, payload }: { teamId: string; payload: AddTeamMemberRequest }, { dispatch }) => {
    const member = await teamsApi.addMember(teamId, payload);
    dispatch(memberAdded(member));
  },
);

export const fetchMyMemberships = createAsyncThunk(
  "teams/fetchMyMemberships",
  async (_: void, { dispatch }) => {
    dispatch(myMembershipsRequestStarted());
    try {
      const memberships = await teamsApi.listMyMemberships();
      dispatch(myMembershipsLoaded(memberships));
    } catch (err) {
      dispatch(myMembershipsRequestFailed(errorMessage(err)));
      throw err;
    }
  },
);

export const removeTeamMember = createAsyncThunk(
  "teams/removeMember",
  async (
    { teamId, userId, role }: { teamId: string; userId: string; role: TeamRole },
    { dispatch },
  ) => {
    await teamsApi.removeMember(teamId, userId, role);
    dispatch(memberRemoved({ teamId, userId, role }));
  },
);
